import moment from "moment";
import React from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import { classNames } from "../../../helpers";
import { userState } from "../../../redux/reducers/userReducer";
import axiosInstance from "../../../utils/axiosInstance";
import { getUrlForBookings, getUrlForClasses } from "../../../utils/confighelpers";
import { Avatar, purchaseName } from "../../classes/members/memberUtils";
import { axisTick, BOOKING_RANGES, bucketLabels, ChartShell, COLORS, EmptyLine, fetchBuckets, findRange, LinkButton, ListSkeleton, Panel, Picker, startDateFor, TooltipBox } from "./shared";

/* ------------------------------------------------------------------ */
/* Schedule (this week + the next one) — feeds "Coming up" and "% full" */
/* ------------------------------------------------------------------ */

export const bookedCount = (s) => (Array.isArray(s.participants) ? s.participants : []).reduce((n, p) => n + (parseInt(p?.bookingQty, 10) || 1), 0);
export const capacityOf = (s) => {
  const cap = parseInt(s.capacity, 10);
  if (cap > 0) return cap;
  return (Array.isArray(s.eventPricingList) ? s.eventPricingList : []).reduce((n, p) => n + (parseInt(p?.capacity, 10) || 0), 0);
};
const isCancelled = (s) => ["cancelled", "refunded"].includes(String(s.eventStatus || "").toLowerCase());
const locationLabel = (s) => s?.location?.addressName || [s?.location?.houseNumber, s?.location?.city].filter(Boolean).join(", ") || "";
const CLASS_COLORS = ["#7e00dd", "#F64E60", "#0F8B8D", "#D97706", "#2563EB", "#5B274B"];
const colorFor = (id) => {
  let h = 0;
  for (const c of String(id || "")) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return CLASS_COLORS[h % CLASS_COLORS.length];
};

/** Sessions from Monday of this week until 14 days later (cancelled sessions left out). */
export function useSchedule() {
  const userReduxState = useSelector(userState);
  const businessId = userReduxState.userData?._id;
  const [sessions, setSessions] = React.useState(null);
  const [error, setError] = React.useState(false);
  const load = React.useCallback(async () => {
    if (!businessId) return;
    setError(false);
    const weekStart = moment().startOf("isoWeek");
    try {
      const res = await axiosInstance({
        method: "get",
        url: `${getUrlForClasses("")}getMyClubSchedule?searchDate=${weekStart.toDate().toISOString()}&businessId=${businessId}&daysbeforeCurrentDate=0&daysAfterCurrentDate=14`,
      });
      if (res?.status === 200 && res?.data?.statusCode === 200) {
        const data = res.data.data || {};
        const flat = Array.isArray(data) ? data : Object.values(data).flat();
        const seen = {};
        setSessions(
          flat
            .filter((s) => s && s.classDate && !seen[s._id] && (seen[s._id] = true) && !isCancelled(s))
            .sort((a, b) => new Date(a.classDate) - new Date(b.classDate))
        );
      } else {
        setSessions([]);
        setError(true);
      }
    } catch (e) {
      setSessions([]);
      setError(true);
    }
  }, [businessId]);
  React.useEffect(() => {
    load();
  }, [load]);

  const weekFill = React.useMemo(() => {
    if (!sessions) return null;
    const end = moment().endOf("isoWeek");
    let booked = 0;
    let cap = 0;
    sessions.forEach((s) => {
      if (moment(s.classDate).isAfter(end)) return;
      const c = capacityOf(s);
      if (c > 0) {
        cap += c;
        booked += Math.min(bookedCount(s), c);
      }
    });
    return cap > 0 ? Math.round((booked / cap) * 100) : null;
  }, [sessions]);

  const today = React.useMemo(() => {
    if (!sessions) return null;
    const list = sessions.filter((s) => moment(s.classDate).isSame(moment(), "day"));
    return { count: list.length, booked: list.reduce((n, s) => n + bookedCount(s), 0) };
  }, [sessions]);

  return { sessions, error, reload: load, weekFill, today };
}

/** Upcoming sessions grouped by day, with a fill bar. */
export function ComingUp({ schedule, limit = 6, subtitle = "Your next sessions and how full they are" }) {
  const navigate = useNavigate();
  const upcoming = (schedule.sessions || []).filter((s) => moment(s.classEndDate || s.classDate).isAfter(moment())).slice(0, limit);
  const groups = [];
  upcoming.forEach((s) => {
    const d = moment(s.classDate);
    const key = d.format("YYYY-MM-DD");
    let g = groups.find((x) => x.key === key);
    if (!g) {
      const label = d.isSame(moment(), "day") ? "Today" : d.isSame(moment().add(1, "day"), "day") ? "Tomorrow" : d.format("dddd");
      g = { key, label: `${label} · ${d.format("ddd D MMM")}`, items: [] };
      groups.push(g);
    }
    g.items.push(s);
  });

  return (
    <Panel title="Coming up" subtitle={subtitle} action={<LinkButton onClick={() => navigate("/myclasses/myschedule")}>My Schedule ›</LinkButton>}>
      {schedule.sessions === null ? (
        <div className="mt-3">
          <ListSkeleton rows={4} />
        </div>
      ) : schedule.error ? (
        <p className="mt-3 text-sm font-semibold text-gray-500">
          Couldn't load your schedule.{" "}
          <button type="button" onClick={schedule.reload} className="font-extrabold text-indigo-bz">
            Try again
          </button>
        </p>
      ) : upcoming.length === 0 ? (
        <div className="mt-3">
          <EmptyLine>No sessions planned for the next two weeks.</EmptyLine>
        </div>
      ) : (
        groups.map((g) => (
          <div key={g.key}>
            <p className="mb-0.5 mt-4 text-[11px] font-extrabold uppercase tracking-wide text-gray-500">{g.label}</p>
            {g.items.map((s) => {
              const b = bookedCount(s);
              const c = capacityOf(s);
              const pct = c > 0 ? Math.min(100, Math.round((b / c) * 100)) : 0;
              const full = c > 0 && b >= c;
              const quiet = c > 0 && !full && pct < 35;
              return (
                <div key={s._id} className="flex items-center gap-3 border-t border-gray-100 py-2.5 first-of-type:border-t-0">
                  <span className="w-12 shrink-0 text-[15px] font-black text-gray-900">{moment(s.classDate).format("HH:mm")}</span>
                  <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: colorFor(s.classId) }} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-extrabold text-gray-900">{s.className || "Class"}</span>
                    <span className="block truncate text-xs text-gray-500">{[s.instructorName, locationLabel(s)].filter(Boolean).join(" · ")}</span>
                  </span>
                  <span className="w-28 shrink-0 sm:w-40">
                    <span className="flex justify-between text-xs font-extrabold text-gray-900">
                      <span>{c > 0 ? `${b}/${c} booked` : `${b} booked`}</span>
                      <span className={full ? "text-[#B4232F]" : quiet ? "text-[#7A4A00]" : "text-gray-500"}>{full ? "Full" : quiet ? "Quiet" : c > 0 ? `${c - b} left` : ""}</span>
                    </span>
                    <span className="mt-1.5 block h-1.5 overflow-hidden rounded-full bg-[#F1EEF2]">
                      <span className={classNames("block h-full rounded-full", full ? "bg-portland-orange" : quiet ? "bg-[#D97706]" : "bg-indigo-bz")} style={{ width: `${pct}%` }} />
                    </span>
                  </span>
                </div>
              );
            })}
          </div>
        ))
      )}
    </Panel>
  );
}

/* ------------------------------------------------------------------ */
/* Recent bookings                                                     */
/* ------------------------------------------------------------------ */

/**
 * Latest bookings and cancellations of the club, newest first; "View all" opens the Bookings page (/bookings) with
 * search, filters and the full list (2026-10-28).
 * GET getMybookings?teacherId&page=1&limit&filterBy=all&listFor (club lists use the backend's fast path).
 */
export function RecentBookings({ memberIndex, limit = 8 }) {
  const navigate = useNavigate();
  const userReduxState = useSelector(userState);
  const businessId = userReduxState.userData?._id;
  const listFor = userReduxState.businessType || "class";
  const [rows, setRows] = React.useState(null);
  const [error, setError] = React.useState(false);

  const load = React.useCallback(async () => {
    if (!businessId) return;
    setRows(null);
    setError(false);
    try {
      const res = await axiosInstance({
        method: "get",
        url: `${getUrlForBookings()}getMybookings?teacherId=${businessId}&page=1&limit=${limit}&filterBy=all&listFor=${listFor}`,
      });
      const data = res?.status === 200 && res?.data?.statusCode === 200 ? res.data.data : null;
      if (!data) throw new Error("bad response");
      setRows(Array.isArray(data.data) ? data.data : []);
    } catch (e) {
      setRows([]);
      setError(true);
    }
  }, [businessId, limit, listFor]);
  React.useEffect(() => {
    load();
  }, [load]);

  return (
    <Panel title="Recent bookings" subtitle="Latest bookings and cancellations" action={<LinkButton onClick={() => navigate("/bookings")}>View all ›</LinkButton>}>
      <div className="mt-2">
        {rows === null ? (
          <ListSkeleton rows={5} />
        ) : rows.length === 0 ? (
          error ? (
            <div className="flex flex-col items-center gap-2 rounded-2xl bg-[#FAF8FB] px-4 py-5 text-center text-sm text-gray-600">
              Couldn't load the bookings.
              <button type="button" onClick={load} className="rounded-xl px-3 py-1.5 text-sm font-extrabold text-indigo-bz hover:bg-[#F3E9FF]">
                Try again
              </button>
            </div>
          ) : (
            <EmptyLine>No bookings yet.</EmptyLine>
          )
        ) : (
          rows.map((b) => {
            const student = b.student_detail || {};
            const member = { _id: student._id || b.studentId, fullName: b.familyMemberName || student.fullName || b.studentName, profilePic: student.profilePic };
            const name = member.fullName || "Member";
            const cancelled = b.status === "Cancelled";
            const when = b.schedule_class_detail?.classDate ? moment(b.schedule_class_detail.classDate) : null;
            // bookingDate is set by the booking code; createdAt is not reliable (models use default: Date.now()).
            const bookedAt = b.bookingDate || b.createdAt;
            const at = moment(cancelled ? b.cancellationDate || b.updatedAt || bookedAt : bookedAt);
            const via = b.purchase_voucher_detail ? purchaseName(b.purchase_voucher_detail) : b.paymentMethod && b.paymentMethod !== "classPass" && b.paymentMethod !== "NA" ? "Paid per class" : "";
            const known = memberIndex?.get(String(member._id || ""));
            const open = known ? () => navigate(`/mymembers/detail?id=${known.consumerId}&mid=${known.memberId}`) : undefined;
            return (
              <div key={b._id} className="flex items-center gap-3 border-t border-gray-100 py-2.5 first:border-t-0">
                <Avatar src={member.profilePic} name={name} size={32} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-extrabold text-gray-900">
                    {open ? (
                      <button type="button" onClick={open} className="font-extrabold hover:text-indigo-bz">
                        {name}
                      </button>
                    ) : (
                      name
                    )}{" "}
                    <span className={classNames("font-semibold", cancelled ? "text-[#B4232F]" : "text-gray-500")}>{cancelled ? "cancelled" : "booked"}</span>
                  </span>
                  <span className="block truncate text-xs text-gray-500">{[b.className || b.schedule_class_detail?.className, when ? when.format("ddd D MMM, HH:mm") : "", via].filter(Boolean).join(" · ")}</span>
                </span>
                <span className="shrink-0 whitespace-nowrap text-xs font-bold text-gray-400">{at.isValid() ? at.fromNow() : ""}</span>
              </div>
            );
          })
        )}
      </div>
    </Panel>
  );
}

/* ------------------------------------------------------------------ */
/* Bookings chart                                                      */
/* ------------------------------------------------------------------ */

export function BookingsChartCard() {
  const userReduxState = useSelector(userState);
  const [rangeKey, setRangeKey] = React.useState("l4w");
  const [classId, setClassId] = React.useState("all");
  const [classes, setClasses] = React.useState([]);
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const reqRef = React.useRef(0);
  const range = findRange(BOOKING_RANGES, rangeKey);

  React.useEffect(() => {
    (async () => {
      try {
        const res = await axiosInstance({ method: "get", url: `${getUrlForClasses()}listOfClasses?isPackage=${false}&listFor=${userReduxState.businessType}&onlyClassDetail=true` });
        if (res?.status === 200 && Array.isArray(res.data?.data)) setClasses(res.data.data.map((c) => ({ value: c._id, label: c.className })));
      } catch (e) {
        /* the filter just shows "All activities" */
      }
    })();
  }, [userReduxState.businessType]);

  const load = React.useCallback(async () => {
    const id = ++reqRef.current;
    setLoading(true);
    setError("");
    try {
      const cls = classId === "all" ? "" : `&classId=${classId}`;
      const base = { interval: range.interval, size: range.size, startDate: startDateFor(range, true) };
      const [confirmed, cancelled] = await Promise.all([fetchBuckets("allBookingStats", { ...base, extra: `${cls}&status=Confirmed` }), fetchBuckets("allBookingStats", { ...base, extra: `${cls}&status=Cancelled` })]);
      if (id !== reqRef.current) return;
      setRows(confirmed.map((b, i) => ({ ...bucketLabels(b, range.interval, range.size), confirmed: b.count || 0, cancelled: cancelled[i]?.count || 0 })));
    } catch (e) {
      if (id === reqRef.current) setError(e.message || "Couldn't load bookings.");
    } finally {
      if (id === reqRef.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeKey, classId]);
  React.useEffect(() => {
    load();
  }, [load]);

  const confirmed = rows.reduce((a, r) => a + r.confirmed, 0);
  const cancelled = rows.reduce((a, r) => a + r.cancelled, 0);
  const period = range.title.toLowerCase();

  return (
    <Panel
      title="Class bookings"
      subtitle={
        <span className="flex flex-wrap items-baseline gap-x-2">
          <span className="text-2xl font-black text-gray-900">{loading ? "–" : confirmed.toLocaleString("nl-NL")}</span>
          <span className="font-bold text-gray-600">{loading ? "" : `confirmed ${range.side === "next" ? "for classes in the" : "in the"} ${period} · ${cancelled} cancelled`}</span>
        </span>
      }
      action={
        <div className="flex flex-wrap gap-2">
          <Picker label="Activity" value={classId} options={[{ value: "all", label: "All activities" }, ...classes]} onChange={setClassId} />
          <Picker label="Bookings period" value={rangeKey} groups={BOOKING_RANGES} onChange={setRangeKey} />
        </div>
      }
    >
      <div className="mt-3 flex gap-4 text-xs font-bold text-gray-700">
        <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-[3px]" style={{ background: COLORS.confirmed }} />Confirmed</span>
        <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-[3px]" style={{ background: COLORS.cancelled }} />Cancelled</span>
      </div>
      <ChartShell loading={loading} error={error} onRetry={load} empty={!loading && confirmed + cancelled === 0} emptyText={`No bookings ${range.side === "next" ? "yet for the" : "in the"} ${period}`} height={280}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={rows} margin={{ top: 8, right: 4, left: -16, bottom: 0 }} barCategoryGap={range.size > 15 ? "18%" : "28%"}>
            <CartesianGrid vertical={false} stroke="#F1EEF2" />
            <XAxis dataKey="short" tick={axisTick} tickLine={false} axisLine={{ stroke: "#E3DCE6" }} interval="preserveStartEnd" minTickGap={8} />
            <YAxis allowDecimals={false} tick={axisTick} tickLine={false} axisLine={false} width={44} />
            <Tooltip cursor={{ fill: "#F7F4F8" }} content={<TooltipBox rows={(d) => [{ label: "Confirmed", value: d.confirmed, color: COLORS.confirmed }, { label: "Cancelled", value: d.cancelled, color: COLORS.cancelled }]} />} />
            <Bar dataKey="confirmed" stackId="b" fill={COLORS.confirmed} maxBarSize={44} isAnimationActive={false} />
            <Bar dataKey="cancelled" stackId="b" fill={COLORS.cancelled} radius={[4, 4, 0, 0]} maxBarSize={44} isAnimationActive={false} />
          </BarChart>
        </ResponsiveContainer>
      </ChartShell>
    </Panel>
  );
}
